import { t } from 'i18next';
import head from 'lodash/head';
import flattenDeep from 'lodash/flattenDeep';

import {
  hasOnDeviceOfType,
  getWaterFlowValue,
  hasOnDeviceOfTypes,
  getValueForDeviceType,
  getWaterPressureValues,
  isVSDCompressor,
  getFirstFlowSensor,
  getFirstPowerSensor,
  getPowerSensorKw,
  getPowerValue,
  useFlowValue,
  useWaterTemperatureValue,
} from '@/comp/MapBuilder/nodes/compressor/CompressorNodeWidget.utils';
import SuperUnit from '@/comp/SuperUnit';
import { metricsDetails } from '@/constants';
import { airTankTypes, compressorTypes, DeviceType, valveTypes } from '@/constants/deviceTypeEnum';
import { MetricType } from '@/constants/metric-type.enum';
import { getFirstDeviceByIndex, toTranslationFormat } from '@/utils/util-functions';
import { MetricsNames } from '@/comp/MapBuilder/MetricsNames';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import unitsEnum from '@/constants/unitsEnum';
import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';
import { useDewPointValue } from '@/comp/MapBuilder/nodes/desiccantAirDryer/DessicantMapView/DesiccantMapView.utils';
import { RefrigeratedAirDryerActivityState } from '@/constants/enums';
import {
  aerationBasinOnDevicesTypes,
  getOnDeviceSensorsToDisplay,
  sensorTypeMapping,
} from '@/comp/MapBuilder/utils/utils';

import { getInverterSpeedUnit, getInverterSpeedValue } from '../AssetInfo.utils';
import CompressorSliders from './Sliders/CompressorSliders';
import BoosterSliders from './Sliders/BoosterSliders';
import DesiccantAirDryerSliders from './Sliders/DesiccantAirDryerSliders';
import ValveSliders from './Sliders/ValveSliders';
import BlowerSliders from './Sliders/BlowerSliders';
import AerationBasinSliders from './Sliders/AerationBasinSliders';
import { startCase } from 'lodash/fp';

export const getInverterSpeedData = (entity, tvalue, t) =>
  isVSDCompressor(entity)
    ? [
        {
          label: t(toTranslationFormat(MetricsNames.InverterSpeed)),
          value: getInverterSpeedValue(entity, tvalue),
          unit: getInverterSpeedUnit(entity, t),
        },
      ]
    : [];

export const getWaterFlowData = (onDevices, tvalue, t) =>
  hasOnDeviceOfType(onDevices, DeviceType.WaterFlowSensor)
    ? [
        {
          label: t(toTranslationFormat(metricsDetails[MetricType.WaterFlowM3Min].text)),
          value: getWaterFlowValue(onDevices, tvalue),
          unit: <SuperUnit unit="m3/min" />,
        },
      ]
    : [];

export const useWaterTemperatureData = (onDevices, _tvalue, t) => {
  const waterInletTemperature = useWaterTemperatureValue(onDevices, MetricType.InletWaterTemperatureCelsius);
  const waterOutletTemperature = useWaterTemperatureValue(onDevices, MetricType.OutletWaterTemperatureCelsius);

  return hasOnDeviceOfType(onDevices, DeviceType.WaterTemperatureSensor)
    ? [
        {
          label: t('Water Temperature In'),
          value: waterInletTemperature,
          unit: <SuperUnit unit="°C" />,
        },
        {
          label: t('Water Temperature Out'),
          value: waterOutletTemperature,
          unit: <SuperUnit unit="°C" />,
        },
      ]
    : [];
};

export const getAmbientTemperatureData = (onDevices, tvalue, t, entity) =>
  hasOnDeviceOfTypes(onDevices, [
    DeviceType.AmbientHumidityAndTemperatureSensor,
    DeviceType.AmbientHumidityAndTemperatureAndDewpointSensor,
    DeviceType.AmbientTemperatureSensor,
  ])
    ? [
        {
          label: t('Ambient Temperature'),
          value: getValueForDeviceType(
            onDevices,
            tvalue,
            [
              DeviceType.AmbientTemperatureSensor,
              DeviceType.AmbientHumidityAndTemperatureSensor,
              DeviceType.AmbientHumidityAndTemperatureAndDewpointSensor,
            ],
            MetricType.ExteriorAmbientTemperatureCelsius,
            entity,
          ),
          unit: <SuperUnit unit="°C" />,
        },
      ]
    : [];

export const getAmbientHumidityData = (onDevices, tvalue, t, entity) =>
  hasOnDeviceOfTypes(onDevices, [
    DeviceType.AmbientHumiditySensor,
    DeviceType.AmbientHumidityAndTemperatureSensor,
    DeviceType.AmbientHumidityAndTemperatureAndDewpointSensor,
  ])
    ? [
        {
          label: t('Ambient Humidity'),
          value: getValueForDeviceType(
            onDevices,
            tvalue,
            [
              DeviceType.AmbientHumiditySensor,
              DeviceType.AmbientHumidityAndTemperatureSensor,
              DeviceType.AmbientHumidityAndTemperatureAndDewpointSensor,
            ],
            MetricType.HumidityPercentage,
            entity,
          ),
          unit: <SuperUnit unit="%" />,
        },
      ]
    : [];

export const getDewpointTemperatureData = (onDevices, tvalue, t, entity) =>
  hasOnDeviceOfTypes(onDevices, [
    DeviceType.DewpointSensor,
    DeviceType.DewpointAndPressureSensor,
    DeviceType.AmbientHumidityAndTemperatureAndDewpointSensor,
  ])
    ? [
        {
          label: t('Dew point temperature'),
          value: getValueForDeviceType(
            onDevices,
            tvalue,
            [
              DeviceType.DewpointSensor,
              DeviceType.DewpointAndPressureSensor,
              DeviceType.AmbientHumidityAndTemperatureAndDewpointSensor,
            ],
            MetricType.DewpointTemperatureCelsius,
            entity,
          ),
          unit: <SuperUnit unit="°C" />,
        },
      ]
    : [];

export const getWaterPressureData = (onDevices, tvalue, t) =>
  hasOnDeviceOfType(onDevices, DeviceType.WaterPressureSensor)
    ? [
        {
          label: t(toTranslationFormat(metricsDetails[MetricType.WaterPressureIn].text)),
          value: getWaterPressureValues(onDevices, tvalue, MetricType.WaterPressureIn),
        },
        {
          label: t(toTranslationFormat(metricsDetails[MetricType.WaterPressureOut].text)),
          value: getWaterPressureValues(onDevices, tvalue, MetricType.WaterPressureOut),
        },
        {
          label: t(toTranslationFormat(metricsDetails[MetricType.WaterPressureDelta].text)),
          value: getWaterPressureValues(onDevices, tvalue, MetricType.WaterPressureDelta),
        },
      ]
    : [];

const getRefrigeratedAirDryerActivityValue = activity => {
  if (!activity || typeof activity !== 'string') {
    return null;
  }
  return Object.values(RefrigeratedAirDryerActivityState).includes(
    activity.toLowerCase() as RefrigeratedAirDryerActivityState,
  )
    ? t(RefrigeratedAirDryerActivityState[activity.toUpperCase()])
    : null;
};

const getAerationBasinOnDevicesData = (onDevices, tvalue, tunit, t) => {
  const onDevicesByType = getOnDeviceSensorsToDisplay(onDevices, [
    ...aerationBasinOnDevicesTypes,
    DeviceType.DissolvedOxygenSensor,
  ]);

  const data = aerationBasinOnDevicesTypes.map((type, i) => {
    const formattedType = type.replace('Sensor', '');
    return onDevicesByType[i].map(sensor => ({
      label: `${sensor.name ? `${sensor.name} | ` : ''}${t(toTranslationFormat(formattedType))}`,
      value: sensorTypeMapping[formattedType]?.value(sensor, tvalue),
      unit: sensorTypeMapping[formattedType]?.unit(tunit, t),
    }));
  });

  return flattenDeep(data);
};

export const useLabelValueRealtimeItemsByDeviceType = entity => {
  const { stationStore } = useStores();
  const { tvalue, tunit, measurementSystem } = useMeasurement();
  const {
    deviceType,
    hp,
    oilTemperatureCelsius,
    airTemperatureCelsius,
    humidityPercentage,
    dewpointTemperatureCelsius,
    pressureBar,
    pipeDiameter,
    tempCelsius,
    activity,
    valveActivity,
    valveAction,
    defaultState,
    valveCurrentPosition,
  } = entity;

  const labelValueRealtimeItems = [];
  const onDevices = stationStore.currentStation?.devices?.filter(device => entity.onDevices.includes(device.deviceId));

  const firstPowerSensor = getFirstPowerSensor(entity, onDevices);
  const powerKw = getPowerValue(entity, stationStore.currentStation?.id, firstPowerSensor?.powerKw, tvalue);
  const ratedHp = tvalue(hp, 'hp', measurementSystem, 'IMPERIAL');

  const firstPowerSensorKw = getPowerSensorKw(firstPowerSensor);
  const firstFlowSensor = getFirstFlowSensor(entity, onDevices);
  const flowM3Min = useFlowValue(entity, firstFlowSensor, firstPowerSensorKw);

  const oilTemperature = tvalue(oilTemperatureCelsius, 'celsius');

  const firstPressureSensor = head(onDevices.filter(device => device.deviceType === 'pressureSensor')) as Entity;
  const dewPoint = useDewPointValue(dewpointTemperatureCelsius, onDevices);
  const [pressureSensors, flowSensors] = getOnDeviceSensorsToDisplay(onDevices, [
    DeviceType.PressureSensor,
    DeviceType.FlowSensor,
  ]);
  const ioRelayDevice = onDevices?.find(device => device.deviceType?.includes('ioRelay'));
  const valveMappedActivity = ioRelayDevice?.valveActivity ?? valveActivity;

  const waterTemperatureData = useWaterTemperatureData(onDevices, tvalue, t);

  if (compressorTypes.includes(deviceType)) {
    labelValueRealtimeItems.push(
      {
        label: t(toTranslationFormat(MetricsNames.Power)),
        value: powerKw,
        unit: t('kW'),
      },
      {
        label: t(toTranslationFormat(MetricsNames.Flow)),
        value: flowM3Min,
        unit: <SuperUnit unit="m3/min" />,
      },
      {
        label: t(toTranslationFormat(MetricsNames.OilTemperature)),
        value: oilTemperature,
        unit: <SuperUnit unit="°C" />,
      },
      {
        label: t(toTranslationFormat(MetricsNames.RatedPower)),
        value: ratedHp,
        unit: <SuperUnit unit="ratedHP" />,
      },
      ...getInverterSpeedData(entity, tvalue, t),
      ...getWaterFlowData(onDevices, tvalue, t),
      ...getAmbientTemperatureData(onDevices, tvalue, t, entity),
      ...getDewpointTemperatureData(onDevices, tvalue, t, entity),
      ...getAmbientHumidityData(onDevices, tvalue, t, entity),
      ...getWaterPressureData(onDevices, tvalue, t),
      ...waterTemperatureData,
    );
  }

  if (deviceType === DeviceType.Blower) {
    labelValueRealtimeItems.push({
      label: t(toTranslationFormat(MetricsNames.Frequency)),
      value: tvalue(entity.frequencyHz, 'Hz'),
      unit: tunit('Hz'),
    });
  } else if (airTankTypes.includes(deviceType)) {
    labelValueRealtimeItems.push({
      label: t('pressure'),
      value: tvalue(firstPressureSensor?.pressureBar, 'bar'),
      unit: t(tunit('Bar')),
    });
  } else if (deviceType === DeviceType.Booster) {
    labelValueRealtimeItems.push(
      {
        label: t(toTranslationFormat(MetricsNames.Power)),
        value: tvalue(firstPowerSensor?.powerKw, 'kw'),
        unit: 'kW',
      },
      {
        label: t(toTranslationFormat(MetricsNames.RatedPower)),
        value: ratedHp,
        unit: <SuperUnit unit="ratedHP" />,
      },
    );
  } else if (deviceType === DeviceType.DesiccantAirDryer) {
    labelValueRealtimeItems.push(
      {
        label: t('temperature'),
        value: dewPoint,
        unit: tunit('°C'),
      },
      {
        label: t('pressure'),
        value: tvalue(pressureBar, 'bar'),
        unit: tunit('bar'),
      },
      {
        label: t('power'),
        value: tvalue(entity.powerKw ?? getFirstDeviceByIndex(onDevices, DeviceType.PowerSensor)?.powerKw, 'kw'),
        unit: t('kW'),
      },
    );
  } else if (deviceType === DeviceType.RefrigeratedAirDryer) {
    labelValueRealtimeItems.push(
      {
        label: t('temperature'),
        value: tvalue(tempCelsius, 'celsius'),
        unit: tunit('°C'),
      },
      {
        label: t('activity'),
        value: getRefrigeratedAirDryerActivityValue(activity),
      },
    );
  } else if (deviceType === DeviceType.AirTemperatureSensor) {
    labelValueRealtimeItems.push(
      {
        label: t(toTranslationFormat(MetricsNames.AirTemperature)),
        value: tvalue(airTemperatureCelsius, unitsEnum.celsius),
        unit: t(tunit('°C')),
      },
      {
        label: t(toTranslationFormat(MetricsNames.Humidity)),
        value: tvalue(humidityPercentage, unitsEnum.percentage),
        unit: tunit('%'),
      },
    );
  } else if (deviceType === DeviceType.FlowSensor) {
    labelValueRealtimeItems.push(
      {
        label: t(toTranslationFormat(MetricsNames.Flow)),
        value: tvalue(entity.flowM3Min, 'm^3/min'),
        unit: <SuperUnit unit="m3/min" />,
      },
      {
        label: t('pipe diameter'),
        value: tvalue(pipeDiameter, 'mm'),
        unit: t(tunit('mm')),
      },
      {
        label: t('pipe material'),
        value: startCase(entity.pipeMaterial),
      },
    );
  } else if (deviceType === DeviceType.PressureSensor) {
    labelValueRealtimeItems.push({
      label: t('pressure'),
      value: tvalue(pressureBar, 'bar'),
      unit: tunit('bar'),
    });
  } else if (deviceType === DeviceType.DewpointSensor) {
    labelValueRealtimeItems.push({
      label: t('temperature'),
      value: tvalue(dewpointTemperatureCelsius, 'celsius'),
      unit: tunit('°C'),
    });
  } else if (deviceType === DeviceType.DewpointAndPressureSensor) {
    labelValueRealtimeItems.push(
      {
        label: t('temperature'),
        value: tvalue(dewpointTemperatureCelsius, 'celsius'),
        unit: tunit('°C'),
      },
      {
        label: t('pressure'),
        value: tvalue(pressureBar, 'bar'),
        unit: tunit('bar'),
      },
    );
  } else if (deviceType === DeviceType.FlowAndPressureSensor) {
    labelValueRealtimeItems.push(
      {
        label: t('pressure'),
        value: tvalue(pressureBar, 'bar'),
        unit: tunit('bar'),
      },
      {
        label: t(toTranslationFormat(MetricsNames.Flow)),
        value: tvalue(entity.flowM3Min, 'm^3/min'),
        unit: <SuperUnit unit="m3/min" />,
      },
      {
        label: t('pipe diameter'),
        value: tvalue(pipeDiameter, 'mm'),
        unit: t(tunit('mm')),
      },
    );
  } else if (deviceType === DeviceType.ProductionFloor) {
    labelValueRealtimeItems.push(
      ...pressureSensors.map(sensor => ({
        label: `${sensor.name ? `${sensor.name} | ` : ''}${t(toTranslationFormat('pressure'))}`,
        value: sensor.pressureBar,
        unit: tunit('bar'),
      })),
      ...flowSensors.map(sensor => ({
        label: `${sensor.name ? `${sensor.name} | ` : ''}${t(toTranslationFormat('flow'))}`,
        value: sensor.flowM3Min,
        unit: <SuperUnit unit="m3/min" />,
      })),
    );
  } else if (valveTypes.includes(deviceType)) {
    labelValueRealtimeItems.push(
      {
        label: t('back indication'),
        value: valveMappedActivity,
      },
      {
        label: t('last command'),
        value: valveAction,
      },
      {
        label: t('default state'),
        value: defaultState,
      },
      {
        label: t('relative position'),
        value: valveCurrentPosition,
        unit: '%',
      },
    );
  } else if (deviceType === DeviceType.AmmoniaAmmoniumSensor) {
    labelValueRealtimeItems.push({
      label: t('ammoniaAmmonium'),
      value: tvalue(entity[MetricType.AmmoniaAmmoniumPPM], 'ppm'),
      unit: t(tunit('ppm')),
    });
  } else if (deviceType === DeviceType.AerationBasin) {
    labelValueRealtimeItems.push(
      {
        label: t('Water Temperature In'),
        value: tvalue(entity[MetricType.InletWaterTemperatureCelsius], '°C'),
        unit: <SuperUnit unit="°C" />,
      },
      {
        label: t('Water Temperature Out'),
        value: tvalue(entity[MetricType.OutletWaterTemperatureCelsius], '°C'),
        unit: <SuperUnit unit="°C" />,
      },
      ...getAerationBasinOnDevicesData(onDevices, tvalue, tunit, t),
    );
  } else if (deviceType === DeviceType.DissolvedOxygenSensor) {
    labelValueRealtimeItems.push({
      label: t('dissolvedOxygen'),
      value: tvalue(entity[MetricType.DissolvedOxygenPPM], 'ppm'),
      unit: t(tunit('ppm')),
    });
  } else if (deviceType === DeviceType.InfluentFlowSensor) {
    labelValueRealtimeItems.push({
      label: t('influentFlow'),
      value: tvalue(entity[MetricType.InfluentFlowM3Hour], 'm3/hour'),
      unit: <SuperUnit unit="m3/hour" />,
    });
  } else if (deviceType === DeviceType.NitrateSensor) {
    labelValueRealtimeItems.push({
      label: t('nitrate'),
      value: tvalue(entity[MetricType.NitratePPM], 'ppm'),
      unit: t(tunit('ppm')),
    });
  } else if (deviceType === DeviceType.NitriteSensor) {
    labelValueRealtimeItems.push({
      label: t('nitrite'),
      value: tvalue(entity[MetricType.NitritePPM], 'ppm'),
      unit: t(tunit('ppm')),
    });
  } else if (deviceType === DeviceType.OxidationReductionPotentialSensor) {
    labelValueRealtimeItems.push({
      label: t('oxidationReductionPotential'),
      value: tvalue(entity[MetricType.OxidationReductionPotentialMV], 'mV'),
      unit: t(tunit('mV')),
    });
  } else if (deviceType === DeviceType.PhSensor) {
    labelValueRealtimeItems.push({
      label: t('pH'),
      value: tvalue(entity[MetricType.PH], 'pH'),
      unit: t(tunit('pH')),
    });
  }

  return labelValueRealtimeItems;
};

export const getSlidersByDeviceType = (entity: Entity) => {
  const { deviceType } = entity;

  if (deviceType === DeviceType.Blower) {
    return <BlowerSliders entity={entity} />;
  } else if (compressorTypes.includes(deviceType as DeviceType)) {
    return <CompressorSliders entity={entity} />;
  } else if (deviceType === DeviceType.Booster) {
    return <BoosterSliders entity={entity} />;
  } else if (deviceType === DeviceType.DesiccantAirDryer) {
    return <DesiccantAirDryerSliders entity={entity} />;
  } else if (valveTypes.includes(deviceType as DeviceType)) {
    return <ValveSliders entity={entity} />;
  } else if (deviceType === DeviceType.AerationBasin) {
    return <AerationBasinSliders entity={entity} />;
  }
};

export const getValveSliderPoints = (valveMinSetpoint, valveMaxSetpoint) => [
  { value: 0, label: 'Close' },
  { value: valveMinSetpoint, label: 'Min' },
  { value: valveMaxSetpoint, label: 'Max' },
  { value: 100, label: 'Open' },
];
